import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {TABLE_ACTIONS_COLUMN_ID} from "~/components/ottopilotDataViewer/columnConfiguration";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

/** Retained large-page screen-formatted PNG/PDF reads; native CSV never uses this path. */
export const OTTOPILOT_EXPORT_LIMIT = 999999;

const NON_EXPORTABLE_COLUMN_IDS = new Set([
    "id",
    "actionButton",
    TABLE_ACTIONS_COLUMN_ID,
    "__checkbox__",
]);

function isSyntheticTableColumnId(columnId: string): boolean {
    return columnId.startsWith("__") === true && columnId.endsWith("__") === true;
}

export function columnIsExportable<T extends {id: Uuid}>(
    col: OttopilotDataViewerController<T>["columns"]["lineItems"][number],
): boolean {
    if (NON_EXPORTABLE_COLUMN_IDS.has(col.id) === true || isSyntheticTableColumnId(col.id) === true) {
        return false;
    }
    const getter = (col as {valueGetter?: unknown}).valueGetter;
    return typeof getter === "function";
}

export type OttopilotExportColumnPredicate<T extends {id: Uuid}> = (
    col: OttopilotDataViewerController<T>["columns"]["lineItems"][number],
) => boolean;

/** PNG/PDF column order follows the table, including drag-reordered columns. */
export function exportColumnsInTableOrder<T extends {id: Uuid}>(
    controller: OttopilotDataViewerController<T>,
    columnIsExportableFn: OttopilotExportColumnPredicate<T> = columnIsExportable,
): Array<OttopilotDataViewerController<T>["columns"]["lineItems"][number]> {
    const exportableColumns = controller.columns.lineItems.filter(columnIsExportableFn);
    return orderColumnsLikeTable(
        exportableColumns,
        controller.tableColumnConfig?.orderedVisibleColumnIds,
    );
}

export function orderColumnsLikeTable<T extends {id: string}>(
    columns: Array<T>,
    orderedVisibleColumnIds: Array<string> | undefined,
): Array<T> {
    if (orderedVisibleColumnIds === undefined || orderedVisibleColumnIds.length === 0) {
        return columns;
    }
    const columnById = new Map(columns.map((column) => [column.id, column]));
    const orderedColumns: Array<T> = [];
    const seenIds = new Set<string>();
    for (const columnId of orderedVisibleColumnIds) {
        const column = columnById.get(columnId);
        if (column === undefined) {
            continue;
        }
        orderedColumns.push(column);
        seenIds.add(column.id);
    }
    for (const column of columns) {
        if (seenIds.has(column.id) === true) {
            continue;
        }
        orderedColumns.push(column);
    }
    return orderedColumns;
}

export async function fetchOttopilotRowsForExport<T extends {id: Uuid}>(
    controller: OttopilotDataViewerController<T>,
    columnIsExportableFn: OttopilotExportColumnPredicate<T> = columnIsExportable,
    options?: {
        limit?: number;
        offset?: number;
    },
): Promise<{
    exportColumns: Array<(typeof controller.columns.lineItems)[number]>;
    loadedRows: Array<T>;
}> {
    const exportColumns = exportColumnsInTableOrder(controller, columnIsExportableFn);
    const loadedRows = await controller.rowsQueryFn({
        filterConfiguration: controller.filterConfiguration,
        sortConfiguration: controller.sortConfiguration,
        searchConfiguration: controller.searchConfiguration,
        offset: options?.offset ?? 0,
        limit: options?.limit ?? OTTOPILOT_EXPORT_LIMIT,
    });
    return {exportColumns, loadedRows};
}
